/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineContent';

const TimelineContentManager: ComponentManagerFn<'TimelineContent'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TimelineContent'>(platform, 'TimelineContent'),
    type: 'TimelineContent',
    name: 'Timeline Content',
    isChildAllowed: (page, item, childType) =>
      (
        ['TriggerOnClick', 'BoxContainer', 'Box', 'Typography'] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {},
    getDisplayText: (item) => 'Timeline content',
    category: 'Forms',
    icon: { iconName: 'text' },
    description: `**Overview**<br/>
The Timeline Content component acts as the container for the actual information displayed next to each step in a **Timeline**. It holds the text, images, or layout elements that describe the event (e.g., "Order Shipped - 5:00 PM").
Use this component to define the "body" of a timeline item.

**1. Supported Content**<br/>
This component is a container designed to hold other structural and text elements.
* **Typography:** For adding titles, dates, and descriptions.
* **Box / BoxContainer:** For creating complex layouts (e.g., a "Card" look with a header and body).
* **TriggerOnClick:** To make the entire content area clickable for navigation or expanding details.

**2. Layout Context**<br/>
In a standard Timeline layout, the components are arranged relative to the vertical axis:
* **Timeline Dot:** The icon or bullet point representing the event.
* **Timeline Connector:** The line connecting the dots.
* **Timeline Content:** The descriptive text or body (this component).
* **Opposite Content:** The time or date label displayed on the opposite side of the connector line.

**3. Best Use Cases**<br/>
* **Status Updates:** Displaying descriptive text like "Package arrived at local facility."
* **Audit Details:** Showing granular logs such as "User 'John' updated field 'Status'" inside a box layout.
* **Event Cards:** Creating a rich card with a title and summary for a "Project Milestone" or "Release Note."`,
  };
};

export default TimelineContentManager;
